import {
  compareAsc,
  eachDayOfInterval,
  isFuture,
  isToday,
  startOfDay,
} from 'date-fns';
import { ReactNode } from 'react';

import { groupBy } from 'lib/arrays';

import CalendarItem from './CalendarItem';

type Props = {
  startDate: Date;
  endDate: Date;
  items: {
    date: Date;
    selected?: boolean;
    highlighted?: boolean;
    node: ReactNode;
  }[];
  showEmptyDays?: boolean;
  expandDate?: Date;
  collapsible?: boolean;
  selectItemAtIndex?: (index: number) => void;
  highlightItemAtIndex?: (index: number) => void;
};

export const Calendar = ({
  startDate,
  endDate,
  items,
  showEmptyDays = false,
  expandDate,
  collapsible,
  selectItemAtIndex,
  highlightItemAtIndex,
}: Props) => {
  const sortedItems = [...items]
    .sort((a, b) => compareAsc(a.date, b.date))
    .map((item, index) => ({ ...item, index }));

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  const itemsMap = groupBy(sortedItems, ({ date }) =>
    startOfDay(new Date(date)).toISOString()
  );

  const days =
    Object.keys(itemsMap).length > 1
      ? eachDayOfInterval({ start: startDate, end: endDate })
      : [startOfDay(startDate)];

  const displayDays = days.filter(
    date => showEmptyDays || itemsMap[date.toISOString()]
  );

  return displayDays.map(date => {
    const dayItems = itemsMap[date.toISOString()] || [];
    const selected = dayItems.some(it => it.selected);
    const highlighted = dayItems.some(it => it.highlighted);
    return (
      <CalendarItem
        key={date.toString()}
        date={date}
        nodes={dayItems.map(({ node }) => node)}
        selected={selected}
        highlighted={highlighted}
        collapsible={collapsible}
        initialExpanded={
          isToday(date) || isFuture(date) || selected || date === expandDate
        }
        onCollapse={() => {
          if (dayItems.length === 0 || (!selected && !highlighted)) return;
          // When a date w/ a selected/highlighted node is collapsed,
          // change selected/highlighted to the 1st item in the next date
          const lastIndexInDay = dayItems[dayItems.length - 1].index;
          const nextIndex = (lastIndexInDay + 1) % items.length;
          if (selected) {
            selectItemAtIndex?.(nextIndex);
          } else {
            highlightItemAtIndex?.(nextIndex);
          }
        }}
      />
    );
  });
};
